import { useState, useEffect } from "react";

export type CRMSortField = "createdAt" | "name" | "lastActivityAt";
export type CRMSortOrder = "asc" | "desc";

export interface CRMSortPref {
  field: CRMSortField;
  order: CRMSortOrder;
}

/**
 * Most-recently-interacted first. `lastActivityAt` is stamped by
 * FeatureTrackingService (meetings, file notes, transcriptions, emails, SMS,
 * e-sign) and by the document write paths, so this orders the client list by
 * real interaction history rather than when the record happened to be created.
 */
export const CRM_SORT_DEFAULT: CRMSortPref = { field: "lastActivityAt", order: "desc" };

export const CRM_SORT_OPTIONS: { label: string; field: CRMSortField; order: CRMSortOrder }[] = [
  { label: "Last Interacted (Recent)", field: "lastActivityAt", order: "desc" },
  { label: "Last Interacted (Oldest)", field: "lastActivityAt", order: "asc"  },
  { label: "Date Added (Newest)",      field: "createdAt", order: "desc" },
  { label: "Date Added (Oldest)",      field: "createdAt", order: "asc"  },
  { label: "Name (A–Z)",               field: "name",      order: "asc"  },
  { label: "Name (Z–A)",               field: "name",      order: "desc" },
];

interface CRMSortable {
  name?: string;
  createdAt?: string;
  lastActivityAt?: string;
}

/**
 * Comparison value for a recency sort.
 *
 * Falls back to `createdAt` when `lastActivityAt` is missing — a client that has
 * never been interacted with, or one created before the field existed. Without
 * this a brand-new client would sort as epoch 0 and drop to the very bottom of
 * the list, which reads as a bug rather than as "no activity yet".
 */
const recencyOf = (row: CRMSortable): number =>
  new Date(row.lastActivityAt || row.createdAt || 0).getTime();

/** Returns a new array sorted by the given preference; never mutates the input. */
export const sortByCRMPref = <T extends CRMSortable>(
  rows: T[],
  pref: CRMSortPref,
): T[] => {
  const { field, order } = pref;
  const sorted = [...rows].sort((a, b) => {
    if (field === "name") {
      return String(a.name || "").localeCompare(String(b.name || ""));
    }
    if (field === "lastActivityAt") {
      return recencyOf(a) - recencyOf(b);
    }
    return new Date(a[field] || 0).getTime() - new Date(b[field] || 0).getTime();
  });
  return order === "asc" ? sorted : sorted.reverse();
};

const STORAGE_KEY = "crmSortPreference";

/**
 * Bumped when the default changes, so a stored preference from an older default
 * doesn't pin existing users to it forever. Users who deliberately picked a sort
 * are still respected — only the untouched default is migrated.
 */
const STORAGE_VERSION = 2;
const VERSION_KEY = "crmSortPreferenceVersion";

export const readStoredPref = (): CRMSortPref => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    const version = Number(localStorage.getItem(VERSION_KEY) || 0);
    if (!stored) return CRM_SORT_DEFAULT;
    // Pre-versioning value: the old default was createdAt/desc, and there is no
    // way to tell an explicit pick from an untouched default, so adopt the new
    // default for that exact pair and keep anything else the user chose.
    if (version < STORAGE_VERSION) {
      if (stored.field === "createdAt" && stored.order === "desc") {
        return CRM_SORT_DEFAULT;
      }
    }
    return stored as CRMSortPref;
  } catch {
    return CRM_SORT_DEFAULT;
  }
};

export const useCRMSort = () => {
  const [sortPref, setSortPref] = useState<CRMSortPref>(readStoredPref);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sortPref));
    localStorage.setItem(VERSION_KEY, String(STORAGE_VERSION));
  }, [sortPref]);

  return [sortPref, setSortPref] as const;
};
